import { CONFIG, stableDeviceId } from './config.js';
import { Diagnostics } from './diagnostics.js';
import { CameraController, measureMonotonicClockResolution } from './media/camera.js';
import { measureDisplayRefreshRate } from './media/fps.js';
import { setupPwaUpdate } from './pwa-update.js';
import { BROADCAST_ADDRESS, encodeEnvelope, FRAME_TYPE, MAX_PAYLOAD_BYTES } from './protocol/envelope.js';
import { describeMessage } from './message-info.js';
import { checkOfflineReady } from './offline-ready.js';
import { evaluateProfileAdmission, PROFILE_CATALOG } from './protocol/profiles.js';
import { chooseTorchOokTiming, torchOokBufferRequirements } from './protocol/torch-ook.js';
import { SpatialReceiver } from './rx/spatial-receiver.js';
import { TorchReceiver } from './rx/torch-receiver.js';
import { SpatialScreenTransmitter } from './tx/spatial-screen.js';
import { TorchTransmitter } from './tx/torch-transmitter.js';

export class LumiChat {
  constructor() {
    this.deviceId = stableDeviceId();
    this.sequence = Number.parseInt(localStorage.getItem('lumichat.sequence') || '0', 10) & 0xff;
    this.profile = localStorage.getItem('lumichat.profile') || CONFIG.profileDefault;
    if (!PROFILE_CATALOG[this.profile]) this.profile = CONFIG.profileDefault;
    const storedEcc = localStorage.getItem('lumichat.ecc');
    this.ecc = ['H', 'Q'].includes(storedEcc) ? storedEcc : CONFIG.qr.errorCorrectionLevel;
    const storedTokenChars = Number(localStorage.getItem('lumichat.tokenChars'));
    this.tokenChars = [16, 24, 48].includes(storedTokenChars) ? storedTokenChars : CONFIG.qr.tokenChars;
    const storedAckTimeoutMs = Number(localStorage.getItem('lumichat.ackTimeoutMs'));
    this.ackTimeoutMs = [2500, 4000, 6000].includes(storedAckTimeoutMs)
      ? storedAckTimeoutMs
      : CONFIG.qr.ackTimeoutMs;
    const storedTokenHoldMs = Number(localStorage.getItem('lumichat.tokenHoldMs'));
    this.tokenHoldMs = [500, 900, 1400].includes(storedTokenHoldMs)
      ? storedTokenHoldMs
      : CONFIG.qr.tokenHoldMs;
    const storedQrFacingMode = localStorage.getItem('lumichat.qrFacingMode');
    this.qrFacingMode = ['user', 'environment'].includes(storedQrFacingMode)
      ? storedQrFacingMode
      : 'user';
    this.selectedCamera = localStorage.getItem(cameraStorageKey(this.profile)) || '';
    this.camera = new CameraController();
    this.currentCameraSelection = null;
    this.receiver = null;
    this.spatialTransmitter = new SpatialScreenTransmitter();
    this.torchTransmitter = null;
    this.diagnostics = new Diagnostics(CONFIG.diagnosticsLimit);
    this.rxEnabled = false;
    this.rxPaused = false;
    this.deliveredSettleFingerprint = null;
    this.deliveredSettleAt = Infinity;
    this.rxStarting = null;
    this.rxGeneration = 0;
    this.cameraWatchdogTimer = null;
    this.lastCameraFrameAt = null;
    this.cameraStalled = false;
    this.cameraReconfiguring = false;
    this.updateReady = false;
    this.updateApplying = false;
    this.lastSuppressedStatusKey = null;
    this.autoReceive = false;
    this.benchRunning = false;
    this.sendRunning = false;
    const storedTxMode = localStorage.getItem('lumichat.txMode');
    this.txMode = ['static', 'arq', 'udp'].includes(storedTxMode) ? storedTxMode : 'static';
    const clockResolutionMs = measureMonotonicClockResolution();
    this.capabilities = {
      measuredCameraFps: null,
      frameIntervalP95Ms: null,
      displayHz: null,
      torchAvailable: false,
      torchToggleP95Ms: null,
      torchBenchmark: null,
      monotonicClock: clockResolutionMs !== null,
      clockResolutionMs,
    };
  }

  init() {
    this.video = byId('camera');
    const buildId = document.querySelector('meta[name="lumichat-build"]')?.content || 'source';
    byId('app-version').textContent = `v${CONFIG.appVersion} · ${buildId}`;
    byId('open-settings').addEventListener('click', () => byId('settings').showModal());
    byId('send').addEventListener('click', () => { void this.send(); });
    byId('message').addEventListener('keydown', (event) => {
      if (event.key === 'Enter') { event.preventDefault(); void this.send(); }
    });
    byId('message').addEventListener('input', () => this.renderComposer());
    byId('prepare-receive')?.addEventListener('click', () => { void this.prepareReceive(); });
    byId('toggle-rx').addEventListener('click', () => { void this.toggleReceiver(); });
    byId('run-bench').addEventListener('click', () => byId('settings').showModal());
    byId('settings-bench').addEventListener('click', () => { void this.runDeviceBench(); });
    byId('export-diagnostics').addEventListener('click', () => this.diagnostics.export());
    byId('apply-update').addEventListener('click', () => { void this.applyReadyUpdate(); });
    byId('quick-profile').addEventListener('click', () => { void this.cycleProfile(); });
    byId('mode-toggle').addEventListener('click', () => { this.cycleTxMode(); });
    document.querySelectorAll('input[name="profile"]').forEach((input) => {
      input.checked = input.value === this.profile;
      input.addEventListener('change', () => { void this.setProfile(input.value); });
    });
    byId('qr-ecc').value = this.ecc;
    byId('qr-ecc').addEventListener('change', (event) => {
      this.ecc = event.target.value;
      localStorage.setItem('lumichat.ecc', this.ecc);
    });
    byId('qr-token-chars').value = String(this.tokenChars);
    byId('qr-token-chars').addEventListener('change', (event) => {
      this.tokenChars = Number(event.target.value);
      localStorage.setItem('lumichat.tokenChars', String(this.tokenChars));
      this.renderComposer();
    });
    byId('ack-timeout-ms').value = String(this.ackTimeoutMs);
    byId('ack-timeout-ms').addEventListener('change', (event) => {
      this.ackTimeoutMs = Number(event.target.value);
      localStorage.setItem('lumichat.ackTimeoutMs', String(this.ackTimeoutMs));
    });
    byId('qr-token-hold-ms').value = String(this.tokenHoldMs);
    byId('qr-token-hold-ms').addEventListener('change', (event) => {
      this.tokenHoldMs = Number(event.target.value);
      localStorage.setItem('lumichat.tokenHoldMs', String(this.tokenHoldMs));
      this.renderAdmission();
      this.renderComposer();
    });
    byId('qr-camera-facing').value = this.qrFacingMode;
    byId('qr-camera-facing').addEventListener('change', async (event) => {
      const requestedFacingMode = event.target.value;
      if (this.sendRunning || this.benchRunning || this.cameraReconfiguring || this.updateApplying) {
        event.target.value = this.qrFacingMode;
        this.toast('Caméra verrouillée pendant le transfert ou le bench', true);
        return;
      }
      this.cameraReconfiguring = true;
      try {
        const receiverShouldRestart = this.profile === 'flashlight'
          ? false
          : await this.quiesceReceiverForReconfigure();
        this.qrFacingMode = requestedFacingMode;
        byId('qr-camera-facing').value = requestedFacingMode;
        localStorage.setItem('lumichat.qrFacingMode', this.qrFacingMode);
        localStorage.removeItem(cameraStorageKey('screen-mono'));
        localStorage.removeItem(cameraStorageKey('screen-color'));
        this.invalidateReceiverProof('qr-camera-facing-changed');
        if (this.profile !== 'flashlight') {
          this.selectedCamera = '';
          byId('camera-select').value = '';
          if (receiverShouldRestart) await this.startReceiver('camera-facing-change');
        }
        this.renderProfile();
      } finally {
        this.cameraReconfiguring = false;
      }
    });
    byId('camera-select').addEventListener('change', async (event) => {
      const requestedCamera = event.target.value;
      if (this.sendRunning || this.benchRunning || this.cameraReconfiguring || this.updateApplying) {
        event.target.value = this.selectedCamera;
        this.toast('Caméra verrouillée pendant le transfert ou le bench', true);
        return;
      }
      this.cameraReconfiguring = true;
      try {
        const receiverShouldRestart = await this.quiesceReceiverForReconfigure();
        this.selectedCamera = requestedCamera;
        byId('camera-select').value = requestedCamera;
        localStorage.setItem(cameraStorageKey(this.profile), this.selectedCamera);
        this.invalidateReceiverProof('camera-selection-changed');
        if (receiverShouldRestart) await this.startReceiver('camera-selection-change');
      } finally {
        this.cameraReconfiguring = false;
      }
    });
    this.diagnostics.set('app', {
      version: CONFIG.appVersion,
      buildId,
      deviceId: this.deviceId,
      profile: this.profile,
      qrLink: 'LQP1-selective-repeat',
    });
    this.renderProfile();
    const updateOffline = () => { void this.renderOfflineStatus(buildId); };
    globalThis.addEventListener('online', updateOffline);
    globalThis.addEventListener('offline', updateOffline);
    navigator.serviceWorker?.addEventListener('controllerchange', updateOffline);
    updateOffline();
    navigator.serviceWorker?.ready?.then(updateOffline).catch(() => {});
    this.measureDisplay().catch((error) => this.diagnostics.event('display_measure_failed', { reason: error.message }));
    this.refreshCameraList().catch(() => {});
    void setupPwaUpdate({
      serviceWorker: navigator.serviceWorker,
      reload: () => this.markUpdateReady('controller-changed'),
      fetchFn: globalThis.fetch?.bind(globalThis),
      expectedBuildId: buildId === 'source' ? null : buildId,
      onDiagnostic: (kind, details) => {
        this.diagnostics.event(kind, details);
        if (kind === 'pwa_manifest_mismatch') {
          this.markUpdateReady('manifest-mismatch');
        }
      },
    });
  }

  async renderOfflineStatus(buildId) {
    const status = byId('offline-status');
    if (!status) return;
    const result = await checkOfflineReady({ cacheStorage: globalThis.caches, buildId });
    status.textContent = result.ready
      ? 'Disponible hors ligne sur cet appareil · garder cet onglet ou installer l’app'
      : 'Hors ligne non prêt · ouvrir une fois avec Internet et attendre la mise en cache';
    status.classList.toggle('ready', result.ready);
  }

  async prepareReceive() {
    if (this.sendRunning || this.benchRunning || this.cameraReconfiguring || this.updateApplying) return;
    if (this.rxPaused) this.resumeScanAfterSettle();
    const facingMode = this.txMode === 'arq' ? 'user' : 'environment';
    if (this.profile !== 'flashlight' && this.qrFacingMode !== facingMode) {
      this.cameraReconfiguring = true;
      try {
        await this.quiesceReceiverForReconfigure();
        this.qrFacingMode = facingMode;
        this.selectedCamera = '';
        localStorage.setItem('lumichat.qrFacingMode', facingMode);
        localStorage.removeItem(cameraStorageKey(this.profile));
        byId('qr-camera-facing').value = facingMode;
        byId('camera-select').value = '';
      } finally {
        this.cameraReconfiguring = false;
      }
    }
    this.autoReceive = true;
    localStorage.setItem('lumichat.autoReceive', 'on');
    await this.startReceiver('user');
    this.renderProfile();
  }

  renderComposer() {
    const info = byId('message-info');
    if (!info) return;
    const details = describeMessage(byId('message').value, {
      mode: this.txMode, profile: this.profile, chunkSize: this.tokenChars,
      holdMs: this.tokenHoldMs, repeats: CONFIG.qr.udpRepeats,
    });
    info.textContent = details.label;
    info.classList.toggle('error', !details.valid);
    byId('message').setAttribute?.('aria-invalid', String(!details.valid));
    byId('send').disabled = this.sendRunning || !details.valid || details.bytes === 0;
  }

  renderTxMode() {
    const names = { static: 'QR fixe', arq: 'Confirmé', udp: 'Répété' };
    const button = byId('mode-toggle');
    button.querySelector('span').textContent = names[this.txMode];
    button.classList.toggle('udp', this.txMode !== 'arq');
    button.disabled = this.profile === 'flashlight';
    byId('send').setAttribute?.('aria-label', this.profile === 'flashlight' ? 'Émettre avec la torche'
      : this.txMode === 'static' ? 'Afficher le texte en QR' : 'Émettre les paquets QR');
    const help = byId('mode-help');
    if (help) help.textContent = this.profile === 'flashlight'
      ? 'Torche expérimentale : réception non confirmée. Commencer par le QR N&B.'
      : this.txMode === 'static'
        ? 'QR fixe : ouvre LumiChat sur l’autre appareil, touche Recevoir et vise l’écran. Ferme le QR après lecture. Si le QR est trop dense, essaie Répété.'
        : this.txMode === 'arq'
          ? 'Confirmé (ARQ) : caméras frontales, écrans face à face sur les deux appareils. Seule une réponse optique confirme le transfert.'
          : 'Répété : petits QR affichés deux fois. Caméra arrière possible. Vérifie le texte sur le destinataire : aucune confirmation automatique.';
    this.renderComposer();
  }

  async measureDisplay() {
    const result = await measureDisplayRefreshRate({ durationMs: 1200, maxFrames: Infinity });
    this.capabilities.displayHz = result.deliveredFps;
    this.diagnostics.set('display', result);
    byId('display-fps').textContent = formatFps(result.deliveredFps);
    this.renderAdmission();
  }

  async refreshCameraList() {
    const cameras = await this.camera.list();
    const select = byId('camera-select');
    const existing = new Set([...select.options].map((option) => option.value));
    for (const camera of cameras) {
      if (existing.has(camera.id)) continue;
      const option = document.createElement('option');
      option.value = camera.id;
      option.textContent = camera.label;
      select.appendChild(option);
    }
    select.value = this.selectedCamera;
  }

  async toggleReceiver() {
    if (this.sendRunning || this.benchRunning || this.cameraReconfiguring || this.updateApplying) {
      this.toast('Caméra verrouillée pendant le transfert ou le bench', true);
      return;
    }
    if (this.rxEnabled && this.rxPaused && this.receiver) {
      this.resumeScanAfterSettle();
      return;
    }
    if (this.cameraStalled) {
      this.autoReceive = true;
      localStorage.setItem('lumichat.autoReceive', 'on');
      await this.stopReceiver();
      await this.startReceiver('stalled-retry');
      return;
    }
    if (this.rxEnabled) {
      this.autoReceive = false;
      localStorage.setItem('lumichat.autoReceive', 'off');
      await this.stopReceiver();
    } else {
      await this.prepareReceive();
    }
  }

  async quiesceReceiverForReconfigure() {
    const shouldRestart = this.rxEnabled || Boolean(this.rxStarting) || this.autoReceive;
    if (this.rxStarting) {
      this.rxGeneration += 1;
      await this.rxStarting;
    }
    if (this.rxEnabled || this.camera.track) await this.stopReceiver();
    return shouldRestart;
  }

  async startReceiver(origin = 'internal') {
    if (this.rxEnabled && this.camera.track && this.receiver) return true;
    if (this.rxStarting) return this.rxStarting;
    const pending = this.openReceiver(origin);
    this.rxStarting = pending;
    try {
      return await pending;
    } finally {
      if (this.rxStarting === pending) this.rxStarting = null;
    }
  }

  async openReceiver(origin) {
    const button = byId('toggle-rx');
    button.disabled = true;
    setLink('Ouverture caméra…', false);
    this.invalidateReceiverProof('camera-track-opening');
    const generation = ++this.rxGeneration;
    const request = {
      profile: this.profile,
      selectedCamera: this.selectedCamera,
      facingMode: this.preferredFacingMode(),
    };
    try {
      const declared = await this.camera.open(this.video, {
        deviceId: request.selectedCamera || undefined,
        facingMode: request.facingMode,
        width: CONFIG.camera.width,
        height: CONFIG.camera.height,
        frameRate: CONFIG.camera.requestedFps,
      });
      if (generation !== this.rxGeneration
          || request.profile !== this.profile
          || request.selectedCamera !== this.selectedCamera
          || request.facingMode !== this.preferredFacingMode()) {
        await this.camera.close();
        this.currentCameraSelection = null;
        setLink('Récepteur à relancer après changement de configuration', false);
        byId('camera-status').textContent = 'Ouverture caméra annulée · configuration modifiée';
        this.diagnostics.event('receiver_start_superseded', { origin, request });
        return false;
      }
      if (request.profile === 'flashlight'
          && declared.appliedFacingMode
          && declared.appliedFacingMode !== 'environment') {
        throw operationError(
          'REAR_CAMERA_REQUIRED',
          'Safari n’a pas appliqué la caméra arrière requise pour Flashlight',
        );
      }
      if (declared.fellBackFromDeviceId) {
        this.selectedCamera = '';
        localStorage.removeItem(cameraStorageKey(this.profile));
      }
      this.renderDeclaredCamera(declared);
      this.currentCameraSelection = declared;
      this.configureReceiver();
      this.rxEnabled = true;
      this.cameraStalled = false;
      this.lastCameraFrameAt = performance.now();
      this.startReceiverLoop();
      this.startCameraWatchdog();
      byId('scan-guide').hidden = this.profile === 'flashlight';
      button.classList.add('live');
      button.querySelector('span').textContent = 'Arrêter RX';
      const cameraLabel = formatCameraSelection(declared, this.profile);
      setLink(`RX ${cameraLabel}`, true);
      byId('camera-status').textContent = cameraLabel;
      this.diagnostics.set('cameraCurrent', { declared, fps: null, probe: 'pending' });
      this.diagnostics.event('receiver_started', {
        profile: this.profile,
        camera: declared.label,
        requestedFacingMode: declared.requestedFacingMode,
        appliedFacingMode: declared.appliedFacingMode,
        fallback: declared.cameraSelection?.fallback || null,
        origin,
      });
      const track = this.camera.track;
      void this.finishReceiverProbe({ generation, track, declared });
      return true;
    } catch (error) {
      await this.camera.close();
      this.currentCameraSelection = null;
      this.receiver?.stop?.();
      this.receiver = null;
      this.rxEnabled = false;
      byId('scan-guide').hidden = true;
      setLink('Récepteur arrêté', false);
      byId('camera-status').textContent = `Échec caméra · ${error.name || 'Error'}`;
      if (origin === 'startup' && ['NotAllowedError', 'SecurityError'].includes(error.name)) {
        this.autoReceive = false;
        localStorage.setItem('lumichat.autoReceive', 'off');
        button.querySelector('span').textContent = 'Autoriser caméra RX';
      }
      this.toast(`Caméra : ${error.message}`, true);
      this.diagnostics.event('receiver_failed', {
        reason: error.message,
        name: error.name || 'Error',
        selection: error.cameraSelection || null,
        origin,
      });
      return false;
    } finally {
      button.disabled = false;
      this.renderAdmission();
    }
  }

  async finishReceiverProbe({ generation, track, declared }) {
    void this.refreshCameraList().catch((error) => {
      this.diagnostics.event('camera_list_failed', { reason: error.message });
    });
    try {
      const fps = await this.camera.measureCurrentFps({ durationMs: 1400 });
      if (generation !== this.rxGeneration || !this.rxEnabled || this.camera.track !== track) return;
      this.applyFpsMeasurement(fps);
      this.diagnostics.set('cameraCurrent', { declared, fps, probe: 'complete' });
      this.diagnostics.event('receiver_fps_measured', {
        fps: fps.deliveredFps,
        stable: fps.stability?.stable === true,
        source: fps.source,
      });
    } catch (error) {
      if (generation !== this.rxGeneration || !this.rxEnabled || this.camera.track !== track) return;
      this.diagnostics.set('cameraCurrent', {
        declared,
        fps: null,
        probe: 'failed',
        probeFailure: error.message,
      });
      this.diagnostics.event('camera_fps_probe_failed', { reason: error.message });
      setLink(`RX ${formatCameraSelection(declared, this.profile)} · FPS non mesuré`, true);
    }
  }

  async stopReceiver() {
    this.rxGeneration += 1;
    this.stopCameraWatchdog();
    this.receiver?.stop?.();
    await this.camera.close();
    this.currentCameraSelection = null;
    this.receiver = null;
    this.torchTransmitter = null;
    this.rxEnabled = false;
    this.rxPaused = false;
    this.deliveredSettleFingerprint = null;
    this.deliveredSettleAt = Infinity;
    this.cameraStalled = false;
    this.lastCameraFrameAt = null;
    this.lastSuppressedStatusKey = null;
    byId('scan-guide').hidden = true;
    this.invalidateReceiverProof('camera-track-closed');
    const button = byId('toggle-rx');
    button.classList.remove('live');
    button.querySelector('span').textContent = 'Démarrer caméra RX';
    setLink('Récepteur arrêté', false);
    byId('camera-status').textContent = 'Caméra arrêtée';
    this.diagnostics.event('receiver_stopped');
  }

  markUpdateReady(reason) {
    this.updateReady = true;
    byId('apply-update').hidden = false;
    this.diagnostics.event('pwa_update_ready', { reason });
    this.toast('Nouvelle version prête · applique-la hors transfert');
  }

  async applyReadyUpdate() {
    if (this.updateApplying) return false;
    this.updateApplying = true;
    try {
      if (this.updateIsBlockedByLinkState()) {
        this.toast('Mise à jour différée : transfert optique en cours', true);
        return false;
      }
      if (this.rxStarting) {
        this.rxGeneration += 1;
        await this.rxStarting;
      }
      if (this.updateIsBlockedByLinkState()) {
        this.toast('Mise à jour différée : transfert optique en cours', true);
        return false;
      }
      if (this.rxEnabled || this.camera.track) await this.stopReceiver();
      this.diagnostics.event('pwa_update_applying');
      globalThis.location.reload();
      return true;
    } finally {
      this.updateApplying = false;
    }
  }

  updateIsBlockedByLinkState() {
    try { this.receiver?.transfers?.prune?.(performance.now()); } catch {}
    const transferSnapshot = this.receiver?.transfers?.snapshot?.() || {};
    const linkStateRetained = (transferSnapshot.activeCount || 0) > 0
      || (transferSnapshot.completedCount || 0) > 0;
    return this.sendRunning || this.benchRunning || this.cameraReconfiguring
      || this.spatialTransmitter?.busy || this.spatialTransmitter?.mode === 'status'
      || linkStateRetained;
  }

  configureReceiver() {
    this.camera.stop();
    this.receiver?.stop?.();
    if (this.profile === 'flashlight') {
      const torchBuffer = torchOokBufferRequirements({
        maximumChipMs: CONFIG.torch.maximumChipMs,
        maximumFps: Math.max(CONFIG.camera.requestedFps, this.capabilities.measuredCameraFps || 0),
      });
      this.receiver = new TorchReceiver(this.video, {
        chipMs: 'auto',
        chipMsMin: CONFIG.torch.minimumChipMs,
        chipMsMax: CONFIG.torch.maximumChipMs,
        chipMsStep: 20,
        maxBufferSamples: torchBuffer.maxBufferSamples,
        maxBufferMs: torchBuffer.maxBufferMs,
      });
      this.receiver.onMessage = (frame) => this.handleMessage(frame, 'Web‑Torch');
      this.receiver.onDiagnostic = (result) => this.handleDecodeDiagnostic(normalizeTorchDiagnostic(result));
      this.receiver.onFrame = (result) => {
        if (result?.ok) this.handleDecodeDiagnostic(normalizeTorchDiagnostic(result));
      };
      return;
    }

    this.receiver = new SpatialReceiver(this.video, {
      ...CONFIG.camera,
      maxActiveTransfers: CONFIG.qr.maxActiveTransfers,
      transferIdleMs: CONFIG.qr.transferIdleMs,
    });
    this.receiver.setProfile(this.profile);
    this.receiver.onFrame = (result) => this.handleDecodeDiagnostic(result);
    this.receiver.onMessage = (frame) => this.handleMessage(frame, this.profile === 'screen-color' ? 'QR pastel' : 'QR N&B');
    this.receiver.onControl = (packet) => this.handleControlPacket(packet);
    this.receiver.onStatusRequired = (status, details) => { void this.handleStatusRequired(status, details); };
  }

  startReceiverLoop() {
    if (!this.receiver || !this.rxEnabled) return;
    if (this.profile === 'flashlight') {
      this.receiver.start({
        onMessage: (frame) => this.handleMessage(frame, 'Web‑Torch'),
        onDiagnostic: (result) => this.handleDecodeDiagnostic(normalizeTorchDiagnostic(result)),
      });
      return;
    }
    this.camera.start((now, metadata) => {
      this.observeCameraFrame(metadata);
      if (!this.rxPaused) {
        void this.receiver?.ingest(now, metadata);
        this.maybePauseScanAfterSettle(now);
      }
    });
  }

  observeCameraFrame(metadata = {}) {
    this.lastCameraFrameAt = performance.now();
    if (!this.cameraStalled) return;
    this.cameraStalled = false;
    byId('toggle-rx').querySelector('span').textContent = 'Arrêter RX';
    const declared = this.camera.describe();
    const label = formatCameraSelection(declared, this.profile);
    byId('camera-status').textContent = label;
    setLink(`RX ${label}`, true);
    this.diagnostics.event('camera_recovered', {
      profile: this.profile,
      presentedFrames: metadata.presentedFrames ?? null,
    });
  }

  startCameraWatchdog() {
    this.stopCameraWatchdog();
    if (this.profile === 'flashlight') return;
    this.cameraWatchdogTimer = setInterval(() => {
      if (!this.rxEnabled || this.rxPaused || this.benchRunning || this.profile === 'flashlight') return;
      const now = performance.now();
      const ageMs = this.lastCameraFrameAt === null ? Infinity : now - this.lastCameraFrameAt;
      const track = this.camera.track;
      const ended = !track || track.readyState === 'ended';
      const mutedTooLong = track?.muted === true && ageMs >= 1_500;
      if (!ended && !mutedTooLong && ageMs < 3_500) return;
      if (this.cameraStalled) return;
      this.cameraStalled = true;
      byId('toggle-rx').querySelector('span').textContent = 'Relancer caméra RX';
      byId('camera-status').textContent = `Caméra bloquée · ${Math.round(ageMs)} ms sans image`;
      setLink('Caméra bloquée · touche Relancer caméra RX', false);
      this.diagnostics.event('camera_stalled', {
        profile: this.profile,
        ageMs,
        trackReadyState: track?.readyState || null,
        muted: track?.muted === true,
      });
    }, 1_000);
  }

  stopCameraWatchdog() {
    if (this.cameraWatchdogTimer !== null) clearInterval(this.cameraWatchdogTimer);
    this.cameraWatchdogTimer = null;
  }

  pauseReceiverLoop() {
    this.rxPaused = true;
    if (this.profile === 'flashlight') this.receiver?.stop?.();
  }

  resumeReceiverLoop() {
    this.rxPaused = false;
    if (this.rxEnabled && this.profile === 'flashlight') this.startReceiverLoop();
  }

  /**
   * After a QR message was delivered, keep scanning until the same QR has been
   * seen again for `stopAfterDeliveryMs`, then pause. A different QR (a new
   * message) cancels the settle guard. Sending stays full-duplex: the guarded
   * RX is never paused while our own transfer is running.
   */
  maybePauseScanAfterSettle(now) {
    if (this.rxPaused || this.profile === 'flashlight') return;
    const fingerprint = this.deliveredSettleFingerprint;
    if (fingerprint === null) return;
    const active = this.receiver?.deduplicator?.activeFingerprint ?? null;
    if (active === null || now < this.deliveredSettleAt) return;
    if (active !== fingerprint) {
      // A different QR is on screen: the previous delivery is settled.
      this.deliveredSettleFingerprint = null;
      this.deliveredSettleAt = Infinity;
      return;
    }
    if (now - this.deliveredSettleAt < CONFIG.qr.stopAfterDeliveryMs) return;
    if (this.sendRunning || this.spatialTransmitter?.busy || this.benchRunning
        || this.cameraReconfiguring || this.updateApplying) return;
    this.pauseScanAfterSettle(fingerprint);
  }

  pauseScanAfterSettle(fingerprint) {
    this.rxPaused = true;
    const button = byId('toggle-rx');
    button.querySelector('span').textContent = 'Reprendre scan';
    setLink('Message reçu · même QR · scan en pause', false);
    this.diagnostics.event('rx_paused_after_delivery', { fingerprint });
    this.toast('Message reçu · scan en pause');
  }

  resumeScanAfterSettle() {
    if (!this.rxPaused) return false;
    this.rxPaused = false;
    this.deliveredSettleFingerprint = null;
    this.deliveredSettleAt = Infinity;
    const button = byId('toggle-rx');
    button.querySelector('span').textContent = this.rxEnabled ? 'Arrêter RX' : 'Démarrer caméra RX';
    setLink('Récepteur actif · scan repris', true);
    if (this.rxEnabled && this.profile === 'flashlight') this.startReceiverLoop();
    this.diagnostics.event('rx_scan_resumed');
    return true;
  }

  cycleTxMode() {
    if (this.sendRunning || this.benchRunning || this.cameraReconfiguring || this.updateApplying
        || this.spatialTransmitter?.busy || this.profile === 'flashlight') return;
    const modes = ['static', 'arq', 'udp'];
    this.txMode = modes[(modes.indexOf(this.txMode) + 1) % modes.length];
    localStorage.setItem('lumichat.txMode', this.txMode);
    this.renderTxMode();
    this.diagnostics.event('tx_mode_changed', { mode: this.txMode });
    this.toast(this.txMode === 'arq'
      ? 'Mode ARQ : confirmation du pair demandée'
      : this.txMode === 'static' ? 'QR fixe : garde l’écran visible jusqu’à lecture'
        : 'Mode Répété : envoi sans confirmation');
  }

  async send() {
    if (this.sendRunning || this.benchRunning || this.cameraReconfiguring || this.updateApplying) {
      this.toast('Canal occupé · attends la fin de l’opération en cours', true);
      return;
    }
    this.resumeScanAfterSettle();
    const input = byId('message');
    const message = input.value.trim();
    if (!message) return;
    const payloadBytes = new TextEncoder().encode(message).length;
    if (payloadBytes > MAX_PAYLOAD_BYTES) {
      this.toast(`Texte trop long : ${payloadBytes}/${MAX_PAYLOAD_BYTES} octets. Les accents et emoji prennent plusieurs octets.`, true);
      return;
    }
    if ((this.txMode || 'arq') === 'arq' && this.profile !== 'flashlight' && this.qrFacingMode !== 'user') {
      this.toast('Émission ARQ impossible en caméra arrière · choisis Frontale · duplex STATUS sur les deux appareils', true);
      return;
    }
    const sequence = this.sequence;
    let encoded;
    try {
      encoded = encodeEnvelope({
        type: FRAME_TYPE.DATA,
        source: this.deviceId,
        destination: BROADCAST_ADDRESS,
        sequence,
        payload: message,
      });
    } catch (error) {
      this.toast(error.message, true);
      return;
    }
    input.blur();
    this.nextSequence();

    this.sendRunning = true;
    const sentMode = this.txMode;
    byId('send').disabled = true;
    this.diagnostics.event('tx_started', {
      profile: this.profile,
      sequence,
      bytes: encoded.bytes.length,
      checksum: encoded.checksum.toString(16),
    });
    try {
      const result = this.profile === 'flashlight'
        ? await this.sendTorch(encoded)
        : await this.sendQr(encoded);
      input.value = '';
      const medium = this.profile === 'flashlight'
        ? `Web‑Torch ${result.timing.chipMs} ms/chip · réception non confirmée`
        : result.mode === 'static'
          ? 'QR fixe affiché · réception non confirmée'
          : sentMode === 'udp' || result.mode === 'udp'
            ? `QR répété ${result.repeats} fois · réception non confirmée`
            : `${this.profile === 'screen-color' ? 'QR pastel' : 'QR N&B'} · réception confirmée · ${result.transfer.total} paquets`;
      this.addMessage('moi', message, `${medium} · #${sequence}`);
      this.toast(this.profile === 'flashlight'
        ? 'Émission torche terminée · réception distante non confirmée'
        : result.mode === 'static' ? 'QR fermé · vérifie le texte sur le destinataire'
          : sentMode === 'udp' || result.mode === 'udp'
          ? 'Message diffusé · réception non confirmée'
          : 'Message transmis et validé par STATUS');
      this.diagnostics.event('tx_finished', summarizeTxResult(result, sequence));
      if (!this.rxEnabled) setLink('Émission terminée · réception non confirmée', false);
    } catch (error) {
      const code = error.code || 'TX_FAILED';
      this.toast(code === 'QR_ARQ_STATUS_UNAVAILABLE'
        ? 'Confirmation non reçue. Le destinataire peut déjà avoir reçu le texte : vérifie avant de renvoyer.'
        : `Émission ${code} : ${error.message}`, true);
      this.diagnostics.event('tx_failed', { sequence, code, reason: error.message });
      if (!this.rxEnabled) setLink('Émission interrompue · vérifie le destinataire', false);
    } finally {
      byId('send').disabled = false;
      this.sendRunning = false;
      this.renderComposer();
      if (this.rxEnabled) setLink('Récepteur actif', true);
    }
  }

  async sendQr(encoded) {
    if (this.txMode === 'static') {
      return this.spatialTransmitter.transmit(encoded.text, {
        mode: 'static', profile: this.profile, errorCorrectionLevel: this.ecc,
        margin: CONFIG.qr.marginModules,
      });
    }
    if (this.txMode === 'udp') {
      this.toast('QR répétés : garde l’écran face à la caméra du destinataire');
      return this.spatialTransmitter.transmit(encoded.text, {
        mode: 'udp',
        repeats: CONFIG.qr.udpRepeats,
        profile: this.profile,
        chunkSize: this.tokenChars,
        tokenHoldMs: this.tokenHoldMs,
        tokenMinimumDisplayMs: CONFIG.qr.tokenMinimumDisplayMs,
        errorCorrectionLevel: this.ecc,
        margin: CONFIG.qr.marginModules,
        onProgress: (progress) => this.handleQrTxProgress(progress),
      });
    }
    if (!this.rxEnabled) {
      const started = await this.startReceiver();
      if (!started) throw operationError('RX_REQUIRED', 'Caméra locale requise pour lire le STATUS du pair');
    } else if (this.profile === 'flashlight') {
      await this.setProfile('screen-mono');
    }
    if (this.currentCameraSelection?.appliedFacingMode === 'environment') {
      throw operationError(
        'QR_DUPLEX_CAMERA_REQUIRED',
        'iOS a replié la caméra sur l’arrière ; sélectionne une frontale fonctionnelle pour le retour STATUS',
      );
    }
    this.toast('ARQ QR : caméras frontales et écrans face à face');
    return this.spatialTransmitter.transmit(encoded.text, {
      profile: this.profile,
      chunkSize: this.tokenChars,
      tokenHoldMs: this.tokenHoldMs,
      tokenMinimumDisplayMs: CONFIG.qr.tokenMinimumDisplayMs,
      statusTimeoutMs: this.ackTimeoutMs,
      maxRounds: CONFIG.qr.maxPacketAttempts,
      maxPollAttempts: CONFIG.qr.maxPacketAttempts,
      errorCorrectionLevel: this.ecc,
      margin: CONFIG.qr.marginModules,
      onProgress: (progress) => this.handleQrTxProgress(progress),
    });
  }

  async sendTorch(encoded) {
    if (!this.camera.track) {
      const started = await this.startReceiver();
      if (!started) throw operationError('CAMERA_REQUIRED', 'Piste caméra requise pour contrôler la torche');
    }
    this.pauseReceiverLoop();
    try {
      if (!this.torchTransmitter || this.torchTransmitter.track !== this.camera.track) {
        this.torchTransmitter = new TorchTransmitter(this.camera.track);
      }
      let benchmark = this.capabilities.torchBenchmark;
      if (!benchmark) {
        setLink('Bench torche ON/OFF…', true);
        benchmark = await this.torchTransmitter.benchmark({
          warmupCycles: 3,
          measuredCycles: 20,
          onProgress: ({ stage, cycle, total }) => {
            if (stage === 'measure' && cycle % 5 === 0) setLink(`Bench torche ${cycle}/${total}`, true);
          },
        });
        this.applyTorchBenchmark(benchmark);
      }
      const receiverFps = this.capabilities.measuredCameraFps || 25;
      const timing = this.torchTransmitter.selectTiming({
        receiverFps,
        frameIntervalP95Ms: this.capabilities.frameIntervalP95Ms,
        benchmark,
        torchSafetyFactor: CONFIG.torch.toggleSafetyFactor,
        targetSamplesPerChip: CONFIG.torch.receiverFramesPerChip,
        minimumChipMs: CONFIG.torch.minimumChipMs,
        maximumChipMs: CONFIG.torch.maximumChipMs,
      });
      setLink(`Web‑Torch ${timing.chipMs} ms/chip · Manchester`, true);
      const result = await this.torchTransmitter.transmit(encoded.bytes, {
        timing,
        onProgress: ({ chipIndex, chipCount }) => {
          if (chipIndex % Math.max(1, Math.floor(chipCount / 20)) === 0) {
            setLink(`Web‑Torch ${Math.round(chipIndex / chipCount * 100)} %`, true);
          }
        },
      });
      this.diagnostics.set('torchLastTransmission', {
        chipMs: timing.chipMs,
        rawBitrateBps: timing.rawBitrateBps,
        elapsedMs: result.elapsedMs,
        transitionCount: result.transitionCount,
        lateness: result.transitionLateness,
        standardConformance: 'none-claimed',
      });
      return result;
    } finally {
      this.resumeReceiverLoop();
    }
  }

  handleQrTxProgress(progress) {
    if (progress.stage === 'DATA' && progress.mode === 'udp') {
      setLink(`UDP TX paquet ${progress.index + 1}/${progress.transfer.total} · ronde ${progress.round}/${progress.repeats}`, true);
    } else if (progress.stage === 'DATA') {
      setLink(`TX paquet ${progress.index + 1}/${progress.transfer.total} · ronde ${progress.round}`, true);
    } else if (progress.stage === 'POLL') {
      setLink(`POLL ${progress.pollAttempt}/${progress.maxPollAttempts} · attente STATUS`, true);
    } else if (progress.stage === 'STATUS') {
      setLink(progress.complete ? 'STATUS complet · CRC global OK' : `${progress.pending.length} paquet(s) manquant(s)`, true);
    }
    this.diagnostics.event('qr_arq', sanitizeArqProgress(progress));
  }

  handleControlPacket(packet) {
    const accepted = this.spatialTransmitter.acceptControl(packet);
    this.diagnostics.event('qr_control', {
      type: packet.type,
      sessionId: packet.sessionId,
      roundId: packet.roundId ?? null,
      acceptedBySender: accepted,
    });
  }

  async handleStatusRequired(status, details) {
    if (this.qrFacingMode !== 'user' || this.currentCameraSelection?.appliedFacingMode === 'environment') {
      const key = `${status.sessionId}:${status.roundId || ''}`;
      if (key !== this.lastSuppressedStatusKey) {
        this.lastSuppressedStatusKey = key;
        this.diagnostics.event('qr_status_suppressed', {
          sessionId: status.sessionId,
          roundId: status.roundId || null,
          reason: 'rear-camera-receive-only',
          received: details.received.length,
          missing: details.missing,
        });
        this.toast('QR reçu en caméra arrière · retour STATUS impossible dans cette géométrie', true);
      }
      return;
    }
    if (this.spatialTransmitter.busy) {
      this.diagnostics.event('qr_status_collision', {
        sessionId: status.sessionId,
        reason: 'local-transfer-active',
      });
      return;
    }
    const shown = await this.spatialTransmitter.showStatus(status, {
      holdMs: CONFIG.qr.ackDisplayMs,
      errorCorrectionLevel: this.ecc,
    });
    this.diagnostics.event('qr_status_shown', {
      sessionId: status.sessionId,
      received: details.received.length,
      missing: details.missing,
      known: details.known,
      shown: shown.ok,
    });
  }

  handleDecodeDiagnostic(result) {
    if (!result) return;
    this.renderScanGuide(result);
    byId('metric-decode').textContent = Number.isFinite(result.decodeMs) ? result.decodeMs.toFixed(0) : '—';
    const neutralCodes = ['NO_2D_CODE', 'QR_PACKET_MISSING', 'ROI_SEARCH', 'QR_SCAN_HEARTBEAT'];
    byId('metric-crc').textContent = result.ok
      ? (result.code?.includes('COMPLETE') || result.code === 'OK' ? 'OK' : 'LINK')
      : neutralCodes.includes(result.code) ? '—' : result.code;
    if (result.code === 'QR_SCAN_HEARTBEAT') {
      this.diagnostics.set('receiverScan', sanitizeDecode(result));
    } else if (result.ok || !neutralCodes.includes(result.code)) {
      this.diagnostics.event('decode', sanitizeDecode(result));
    }
  }

  renderScanGuide(result) {
    const guide = byId('scan-guide');
    if (!this.rxEnabled || this.profile === 'flashlight') {
      guide.hidden = true;
      return;
    }
    guide.hidden = false;
    const size = qrGeometrySize(result.location);
    const locked = Number.isFinite(size);
    guide.classList.toggle('locked', locked);
    if (locked) {
      guide.querySelector('span').textContent = `QR détecté · ${Math.round(size)} px dans l’analyse`;
      return;
    }
    const scans = result.rxStats?.scans;
    guide.querySelector('span').textContent = Number.isInteger(scans)
      ? `Caméra active · ${scans} scans · aucun QR détecté`
      : 'Caméra active · cadre entièrement le QR';
  }

  handleMessage(frame, medium = null) {
    if (frame.destination !== BROADCAST_ADDRESS && frame.destination !== this.deviceId) {
      this.diagnostics.event('frame_other_destination', { destination: frame.destination, fingerprint: frame.fingerprint });
      return;
    }
    if (frame.type !== FRAME_TYPE.DATA) return;
    const from = frame.source.toString(16).toUpperCase().padStart(4, '0');
    const label = medium || (this.profile === 'screen-color' ? 'QR pastel' : this.profile === 'flashlight' ? 'Web‑Torch' : 'QR N&B');
    this.addMessage(from, frame.message, `${label} · CRC32C ${frame.fingerprint}`);
    this.diagnostics.event('message_received', {
      source: frame.source,
      sequence: frame.sequence,
      bytes: frame.payload.length,
      fingerprint: frame.fingerprint,
      medium: label,
    });
    this.toast(`Message optique reçu de ${from}`);
    // QR profiles only: arm the settle guard. If the same delivered QR stays
    // on screen for ~1 s, the scanner pauses and the user resumes with the RX
    // button (or by sending a message — full-duplex keeps our own TX alive).
    if (this.profile !== 'flashlight' && frame.fingerprint) {
      this.deliveredSettleFingerprint = frame.fingerprint;
      this.deliveredSettleAt = performance.now();
      this.diagnostics.event('delivery_settle_armed', { fingerprint: frame.fingerprint });
    }
  }

  async runDeviceBench() {
    if (this.benchRunning || this.sendRunning || this.cameraReconfiguring || this.updateApplying) return;
    this.benchRunning = true;
    const output = byId('bench-output');
    output.textContent = 'Mesure du refresh écran…';
    try {
      if (this.rxStarting) await this.rxStarting;
      await this.measureDisplay();
      this.receiver?.stop?.();
      this.camera.stop();
      if (!this.camera.track) {
        this.invalidateReceiverProof('device-benchmark-opening-track');
        const declared = await this.camera.open(this.video, {
          deviceId: this.selectedCamera || undefined,
          facingMode: this.preferredFacingMode(),
          width: CONFIG.camera.width,
          height: CONFIG.camera.height,
          frameRate: CONFIG.camera.requestedFps,
        });
        if (this.profile === 'flashlight'
            && declared.appliedFacingMode
            && declared.appliedFacingMode !== 'environment') {
          throw operationError(
            'REAR_CAMERA_REQUIRED',
            'Safari n’a pas appliqué la caméra arrière requise pour mesurer la torche',
          );
        }
        if (declared.fellBackFromDeviceId) {
          this.selectedCamera = '';
          localStorage.removeItem(cameraStorageKey(this.profile));
          await this.refreshCameraList();
        }
        this.renderDeclaredCamera(declared);
      } else {
        this.invalidateReceiverProof('device-benchmark-reconfiguring-track');
      }
      const fpsBench = await this.camera.benchmarkMaximumFps({
        durationMs: 900,
        onProgress: ({ index, total, candidate }) => {
          output.textContent = `FPS ${index + 1}/${total} · ${candidate.width}×${candidate.height} demandé ${candidate.requestedFps}`;
        },
      });
      const selected = fpsBench.best || fpsBench.peak;
      if (fpsBench.best) this.applyFpsMeasurement(fpsBench.best.measurement);

      let torch;
      if (this.profile === 'flashlight') {
        output.textContent = 'Mesure torch : chauffe puis 20 ON + 20 OFF…';
        try {
          this.torchTransmitter = new TorchTransmitter(this.camera.track);
          torch = await this.torchTransmitter.benchmark({
            warmupCycles: 3,
            measuredCycles: 20,
            onProgress: ({ stage, cycle, total }) => {
              output.textContent = stage === 'warmup'
                ? `Torch chauffe ${cycle}/${total}`
                : `Torch mesure ${cycle}/${total} · ON/OFF`;
            },
          });
          this.applyTorchBenchmark(torch);
        } catch (error) {
          torch = { available: false, reason: error.message, code: error.code || 'TORCH_UNAVAILABLE' };
          this.capabilities.torchAvailable = false;
          this.capabilities.torchToggleP95Ms = null;
          this.capabilities.torchBenchmark = null;
        }
      } else {
        torch = { available: false, skipped: true, reason: 'Sélectionne Flashlight pour mesurer la caméra arrière et la torche' };
      }
      this.diagnostics.set('deviceBenchmark', {
        fps: fpsBench,
        torch,
        displayHz: this.capabilities.displayHz,
      });
      output.textContent = formatBench(fpsBench, torch, this.capabilities);
      this.renderAdmission();
      if (this.rxEnabled) {
        this.configureReceiver();
        this.startReceiverLoop();
      } else {
        await this.camera.close();
        this.torchTransmitter = null;
      }
      this.toast(`Bench terminé${selected && !fpsBench.best ? ' · FPS variable, non admis comme stable' : ''}`);
    } catch (error) {
      output.textContent += `\nÉCHEC: ${error.message}`;
      this.diagnostics.event('device_bench_failed', { reason: error.message });
      this.toast(`Bench : ${error.message}`, true);
      if (this.rxEnabled) {
        try {
          if (!this.camera.track) throw operationError('CAMERA_TRACK_LOST', 'piste caméra perdue pendant le bench');
          this.configureReceiver();
          this.startReceiverLoop();
          setLink('Récepteur actif · bench interrompu', true);
        } catch (resumeError) {
          this.diagnostics.event('receiver_resume_failed', { reason: resumeError.message });
          await this.stopReceiver();
        }
      } else {
        await this.camera.close();
      }
    } finally {
      this.benchRunning = false;
    }
  }

  applyFpsMeasurement(fps) {
    this.capabilities.measuredCameraFps = fps.deliveredFps;
    this.capabilities.frameIntervalP95Ms = fps.intervalMs?.p90 ?? fps.intervalMs?.max ?? null;
    this.renderMeasuredFps(fps);
  }

  applyTorchBenchmark(benchmark) {
    this.capabilities.torchAvailable = true;
    this.capabilities.torchToggleP95Ms = benchmark.directionalP95Ms;
    this.capabilities.torchBenchmark = benchmark;
    this.renderAdmission();
    this.renderTxMode();
  }

  renderDeclaredCamera(declared) {
    byId('cap-fps').textContent = declared.capabilities.frameRate?.max
      ? `${formatFps(declared.capabilities.frameRate.max)} max`
      : 'non publié';
    byId('set-fps').textContent = declared.settings.frameRate ? formatFps(declared.settings.frameRate) : 'non publié';
    byId('camera-status').textContent = formatCameraSelection(declared, this.profile);
  }

  invalidateReceiverProof(reason) {
    this.capabilities.measuredCameraFps = null;
    this.capabilities.frameIntervalP95Ms = null;
    this.capabilities.torchAvailable = false;
    this.capabilities.torchToggleP95Ms = null;
    this.capabilities.torchBenchmark = null;
    this.diagnostics.event('receiver_proof_invalidated', { reason });
    this.renderAdmission();
  }

  renderMeasuredFps(fps) {
    byId('real-fps').textContent = `${formatFps(fps.deliveredFps)} ${fps.stability.stable ? 'stable' : 'variable'}`;
    byId('metric-fps').textContent = formatFps(fps.deliveredFps);
  }

  async setProfile(profile) {
    if (!PROFILE_CATALOG[profile] || profile === this.profile) return;
    if (this.sendRunning || this.benchRunning || this.cameraReconfiguring || this.updateApplying) {
      document.querySelector(`input[name="profile"][value="${this.profile}"]`).checked = true;
      this.toast('Attends la fin du transfert ou du bench avant de changer de profil', true);
      return;
    }
    this.cameraReconfiguring = true;
    try {
      const receiverShouldRestart = await this.quiesceReceiverForReconfigure();
      this.profile = profile;
      localStorage.setItem('lumichat.profile', profile);
      this.selectedCamera = localStorage.getItem(cameraStorageKey(profile)) || '';
      byId('camera-select').value = this.selectedCamera;
      this.diagnostics.set('profile', profile);
      this.renderProfile();
      if (receiverShouldRestart) await this.startReceiver('profile-change');
    } finally {
      this.cameraReconfiguring = false;
    }
  }

  preferredFacingMode() {
    return this.profile === 'flashlight' ? 'environment' : this.qrFacingMode;
  }

  async cycleProfile() {
    const ids = ['screen-mono', 'screen-color', 'flashlight'];
    await this.setProfile(ids[(ids.indexOf(this.profile) + 1) % ids.length]);
    document.querySelector(`input[name="profile"][value="${this.profile}"]`).checked = true;
  }

  renderProfile() {
    const names = { 'screen-mono': 'N&B', 'screen-color': 'PASTEL', flashlight: 'LAMPE' };
    byId('metric-profile').textContent = names[this.profile];
    byId('quick-profile').querySelector('span').textContent = names[this.profile];
    byId('settings-bench').textContent = this.profile === 'flashlight'
      ? 'Mesurer caméra arrière + torche'
      : `Mesurer caméra ${this.qrFacingMode === 'user' ? 'frontale duplex' : 'arrière RX simple'} + écran`;
    this.renderAdmission();
    this.renderTxMode();
  }

  renderAdmission() {
    const qrRateHz = 1000 / this.tokenHoldMs;
    const mono = evaluateProfileAdmission('screen-mono', this.capabilities, { codeHz: qrRateHz });
    const color = evaluateProfileAdmission('screen-color', this.capabilities, { codeHz: qrRateHz });
    const flashlight = evaluateProfileAdmission('flashlight', this.capabilities, { symbolHz: 10 });
    const monoLabel = byId('mono-admission');
    const colorLabel = byId('color-admission');
    const torchLabel = byId('torch-admission');
    monoLabel.textContent = mono.admissible ? 'cadence RX compatible · ARQ à prouver' : 'TX prêt · RX à mesurer';
    colorLabel.textContent = color.admissible ? 'cadence RX compatible · ARQ à prouver' : 'TX prêt · RX à mesurer';
    torchLabel.textContent = this.capabilities.torchToggleP95Ms == null
      ? 'mesure requise'
      : `adaptatif · p95 ${this.capabilities.torchToggleP95Ms.toFixed(1)} ms`;
    monoLabel.style.color = mono.admissible ? 'var(--mint)' : 'var(--amber)';
    colorLabel.style.color = color.admissible ? 'var(--mint)' : 'var(--amber)';
    torchLabel.style.color = flashlight.admissible ? 'var(--mint)' : 'var(--amber)';
    this.diagnostics.set('profileAdmission', {
      configuredQrRateHz: qrRateHz,
      mono,
      color,
      flashlight,
      torchStandardConformance: 'none-claimed',
    });
  }

  addMessage(who, text, meta) {
    const article = document.createElement('article');
    article.className = `message ${who === 'moi' ? 'mine' : ''}`;
    const small = document.createElement('small');
    small.textContent = `${who} · ${meta}`;
    const paragraph = document.createElement('p');
    paragraph.textContent = text;
    article.append(small, paragraph);
    const copy = document.createElement('button');
    copy.type = 'button';
    copy.className = 'message-copy';
    copy.textContent = 'Copier';
    copy.setAttribute('aria-label', 'Copier le message');
    copy.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(text);
        this.toast('Message copié');
      } catch {
        this.toast('Copie indisponible : sélectionne le texte du message pour le copier.', true);
      }
    });
    article.append(copy);
    byId('chat-log').appendChild(article);
    article.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }

  nextSequence() {
    const value = this.sequence;
    this.sequence = (this.sequence + 1) & 0xff;
    localStorage.setItem('lumichat.sequence', String(this.sequence));
    return value;
  }

  toast(message, error = false) {
    const toast = byId('toast');
    toast.textContent = message;
    toast.classList.toggle('error', error);
    toast.classList.add('visible');
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => toast.classList.remove('visible'), 5000);
  }
}

function byId(id) { return document.getElementById(id); }
function cameraStorageKey(profile) { return `lumichat.camera.${profile}`; }
function qrGeometrySize(location) {
  if (!location) return null;
  const points = [location.topLeftCorner, location.topRightCorner, location.bottomRightCorner, location.bottomLeftCorner];
  if (points.some((point) => !Number.isFinite(point?.x) || !Number.isFinite(point?.y))) return null;
  const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  return Math.min(distance(points[0], points[1]), distance(points[1], points[2]), distance(points[2], points[3]), distance(points[3], points[0]));
}
function formatFps(value) { return Number.isFinite(value) ? value.toFixed(value >= 100 ? 0 : 1) : '—'; }
function setLink(label, live) {
  byId('link-label').textContent = label;
  byId('link-dot').classList.toggle('live', live);
}
function formatCameraSelection(declared, profile) {
  const facing = declared?.appliedFacingMode
    || declared?.cameraSelection?.applied?.facingMode
    || declared?.settings?.facingMode
    || null;
  const name = facing === 'user'
    ? 'frontale'
    : facing === 'environment'
      ? 'arrière'
      : (declared?.label || 'active');
  if (profile !== 'flashlight' && facing === 'environment') {
    return `caméra ${name} · RX QR simple (STATUS non garanti)`;
  }
  if (profile !== 'flashlight' && facing === 'user') return `caméra ${name} · duplex QR`;
  return `caméra ${name}`;
}
function sanitizeDecode(result) {
  const geometrySizePx = qrGeometrySize(result.location);
  return {
    ok: result.ok,
    code: result.code,
    observedCode: result.observedCode,
    diagnosticOnly: result.diagnosticOnly === true,
    reason: result.reason,
    decodeMs: result.decodeMs,
    width: result.width,
    height: result.height,
    geometrySizePx,
    geometryFraction: Number.isFinite(geometrySizePx) && Number.isFinite(result.width)
      ? geometrySizePx / result.width
      : null,
    qrGeometry: result.qrGeometry || null,
    videoWidth: result.videoWidth,
    videoHeight: result.videoHeight,
    sourceWidth: result.sourceWidth,
    sourceHeight: result.sourceHeight,
    scanWidth: result.scanWidth,
    scanHeight: result.scanHeight,
    packetType: result.packetType,
    sessionId: result.sessionId,
    index: result.index,
    total: result.total,
    received: result.received?.length,
    missing: result.missing,
    fingerprint: result.frame?.fingerprint || result.envelope?.fingerprint,
    chipMs: result.chipMs,
    estimatedChipMs: result.estimatedChipMs,
    rxStats: result.rxStats || null,
  };
}
function normalizeTorchDiagnostic(result) {
  if (!result) return null;
  const decode = result.decode || result;
  return {
    ...result,
    ok: Boolean(result.ok || decode.ok),
    code: decode.code || result.code,
    reason: decode.reason || result.reason,
    decodeMs: result.decodeMs ?? null,
    envelope: decode.envelope || result.envelope,
    estimatedChipMs: result.estimatedChipMs ?? result.timingSearch?.estimatedChipMs ?? null,
  };
}
function sanitizeArqProgress(progress) {
  return {
    stage: progress.stage,
    mode: progress.mode ?? null,
    repeats: progress.repeats ?? null,
    round: progress.round,
    index: progress.index,
    total: progress.transfer?.total,
    pollAttempt: progress.pollAttempt,
    pending: progress.pending,
    acknowledged: progress.acknowledged,
    complete: progress.complete,
    failureCode: progress.failureCode,
  };
}
function summarizeTxResult(result, sequence) {
  if (result.profile?.includes?.('TORCH')) {
    return {
      sequence,
      profile: result.profile,
      chipMs: result.timing.chipMs,
      elapsedMs: result.elapsedMs,
      transitions: result.transitionCount,
      standardConformance: result.standardConformance,
    };
  }
  return {
    sequence,
    profile: result.profile,
    mode: result.mode || 'arq',
    confirmed: result.code === 'QR_ARQ_COMPLETE',
    packets: result.transfer?.total ?? (result.mode === 'static' ? 1 : null),
    dataDisplays: result.dataDisplayCount,
    retransmittedData: result.retransmittedDataCount,
    rounds: result.arq?.round ?? result.repeats ?? null,
    elapsedMs: result.elapsedMs,
  };
}
function formatBench(fpsBench, torch, capabilities) {
  const best = fpsBench.best;
  const peak = fpsBench.peak;
  const lines = [
    `Écran livré : ${formatFps(capabilities.displayHz)} Hz`,
    peak
      ? `Caméra pic observé : ${formatFps(peak.measurement.deliveredFps)} fps (${peak.measurement.stability.stable ? 'stable' : 'variable'})`
      : 'Caméra : aucun profil mesurable',
    best
      ? `Maximum stable retenu : ${formatFps(best.measurement.deliveredFps)} fps (${best.appliedSettings?.width || best.width}×${best.appliedSettings?.height || best.height})`
      : 'Maximum stable : NON PROUVÉ',
    `Profils FPS tentés : ${fpsBench.cases.filter((entry) => entry.ok).length}/${fpsBench.cases.length}`,
  ];
  if (torch?.directionalP95Ms) {
    lines.push(`Torch ON p95 ${torch.on.p95Ms.toFixed(2)} ms · OFF p95 ${torch.off.p95Ms.toFixed(2)} ms`);
    try {
      const timing = chooseTorchOokTiming({
        torchP95Ms: torch.directionalP95Ms,
        receiverFps: capabilities.measuredCameraFps || 25,
        frameIntervalP95Ms: capabilities.frameIntervalP95Ms,
        torchSafetyFactor: CONFIG.torch.toggleSafetyFactor,
        targetSamplesPerChip: CONFIG.torch.receiverFramesPerChip,
        minimumChipMs: CONFIG.torch.minimumChipMs,
        maximumChipMs: CONFIG.torch.maximumChipMs,
      });
      lines.push(`Web‑Torch auto : ${timing.chipMs} ms/chip · ${timing.rawBitrateBps.toFixed(2)} bit/s brut Manchester`);
    } catch (error) {
      lines.push(`Web‑Torch RX : NON COMPATIBLE (${error.code || 'TIMING'} · ${error.message})`);
    }
    lines.push('Web‑Torch : expérimental, transition lumineuse à confirmer sur deux appareils');
    lines.push('C‑OOK 2,2/4,4 kHz : référence IEEE, NON implémenté par l’API Web');
  } else {
    lines.push(`Torch : indisponible (${torch?.reason || 'non mesurée'})`);
  }
  return lines.join('\n');
}
function operationError(code, message) {
  const error = new Error(message);
  error.code = code;
  return error;
}

if (typeof document !== 'undefined') new LumiChat().init();
